import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import MonthPickerDropdown from './MonthPickerDropdown';
import type { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { ANALYTICS_CONSTANTS } from '../../constants';
import { useDateRange } from '../hooks/useDateRange';
import { usePanelPosition } from '../hooks/usePanelPosition';

const pickerButtonStyles =
  'rounded-md border px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm transition-colors';

interface MonthRangeControlsProps {
  readonly filters: BaseAnalyticsFilter;
  readonly onChange: (patch: Partial<BaseAnalyticsFilter>) => void;
}

export function MonthRangeControls({ filters, onChange }: MonthRangeControlsProps) {
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const startRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const panelOpts = { maxWidth: 280, panelHeight: 320 };
  const startPanelPos = usePanelPosition(startRef, showStart, panelOpts);
  const endPanelPos = usePanelPosition(endRef, showEnd, panelOpts);
  const { formatCompact, getMonthStart, getMonthEnd } = useDateRange();

  const earliestDataDate = new Date(ANALYTICS_CONSTANTS.EARLIEST_DATA_YEAR, 0, 1);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (startRef.current && !startRef.current.contains(event.target as Node)) {
        setShowStart(false);
      }
      if (endRef.current && !endRef.current.contains(event.target as Node)) {
        setShowEnd(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const startPanelClass =
    startPanelPos.type === 'fixed' ? 'fixed z-50' : 'absolute left-0 top-full z-50 mt-1';
  const startPanelStyle =
    startPanelPos.type === 'fixed'
      ? { left: startPanelPos.left, top: startPanelPos.top, width: startPanelPos.width }
      : undefined;

  const endPanelClass =
    endPanelPos.type === 'fixed' ? 'fixed z-50' : 'absolute left-0 top-full z-50 mt-1';
  const endPanelStyle =
    endPanelPos.type === 'fixed'
      ? { left: endPanelPos.left, top: endPanelPos.top, width: endPanelPos.width }
      : undefined;

  return (
    <div className="flex items-center space-x-1">
      <div className="relative" ref={startRef}>
        <button
          onClick={() => setShowStart(prev => !prev)}
          className={`${pickerButtonStyles} ${filters.startDate ? 'text-white' : 'border-white/20 text-slate-300 hover:bg-[var(--anl-surface-elev)]'}`}
          style={
            filters.startDate
              ? { backgroundColor: 'var(--anl-warning)', borderColor: 'var(--anl-warning)' }
              : { backgroundColor: '#454b4d' }
          }
        >
          <span className="flex items-center space-x-1">
            <span>{formatCompact(filters.startDate)}</span>
            <ChevronDown
              size={12}
              className={`transition-transform ${showStart ? 'rotate-180' : ''}`}
            />
          </span>
        </button>

        {showStart && (
          <div className={startPanelClass} style={startPanelStyle}>
            <MonthPickerDropdown
              selectedDate={filters.startDate}
              onDateSelect={date => {
                onChange({ startDate: getMonthStart(date) });
                setShowStart(false);
              }}
              minDate={earliestDataDate}
              maxDate={filters.endDate}
            />
          </div>
        )}
      </div>

      <span className="text-sm text-slate-400">to</span>

      <div className="relative" ref={endRef}>
        <button
          onClick={() => setShowEnd(prev => !prev)}
          className={`${pickerButtonStyles} ${filters.endDate ? 'text-white' : 'border-white/20 text-slate-300 hover:bg-[var(--anl-surface-elev)]'}`}
          style={
            filters.endDate
              ? { backgroundColor: 'var(--anl-warning)', borderColor: 'var(--anl-warning)' }
              : { backgroundColor: '#454b4d' }
          }
        >
          <span className="flex items-center space-x-1">
            <span>{formatCompact(filters.endDate)}</span>
            <ChevronDown
              size={12}
              className={`transition-transform ${showEnd ? 'rotate-180' : ''}`}
            />
          </span>
        </button>

        {showEnd && (
          <div className={endPanelClass} style={endPanelStyle}>
            <MonthPickerDropdown
              selectedDate={filters.endDate}
              onDateSelect={date => {
                onChange({ endDate: getMonthEnd(date) });
                setShowEnd(false);
              }}
              minDate={filters.startDate || earliestDataDate}
            />
          </div>
        )}
      </div>
    </div>
  );
}

MonthRangeControls.displayName = 'MonthRangeControls';
